import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Popover,
  Stack,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import { t } from "i18next";
import { FilterTag } from "./FilterTag";
import { TreatmentType } from "@keita/lanepatrol-sdk";
import { useStores } from "../../../../shared/stores/RootStore";
import { CustomPopover } from "../../../../shared/views/CustomPopover";

const TreatmentFiltersBase: React.FC = () => {
  const { filterStore } = useStores();
  const { open, onToggle, onClose } = useDisclosure();
  const [selectedItems, setSelectedItems] = useState(new Set<TreatmentType>());

  useEffect(() => {
    if (open) {
      setSelectedItems(() => new Set(filterStore.treatmentFilters));
    }
    //eslint-disable-next-line
  }, [open]);

  const handleClear = () => {
    setSelectedItems(new Set());
  };

  const handleSubmit = () => {
    filterStore.setTreatmentFilters([...selectedItems]);
    onClose();
  };

  const removeActiveFilter = (type: TreatmentType) => {
    filterStore.setTreatmentFilters(
      filterStore.treatmentFilters.filter((item) => item !== type)
    );
  };

  return (
    <Stack>
      <CustomPopover
        open={open}
        onClose={onClose}
        trigger={
          <Button
            fontSize={"sm"}
            size="sm"
            variant="outline"
            colorPalette="primary"
            h="unset"
            py={1.5}
            onClick={onToggle}
          >
            {t("map:filters.byTreatmentName")}
          </Button>
        }
        minW="620px"
        maxH="360px"
      >
        <Popover.Header fontWeight={"semibold"}>
          {t("map:filters.byTreatmentTitle")}
        </Popover.Header>
        <Popover.Body overflowY="auto">
          <VStack alignItems="start">
            {Object.values(TreatmentType).map((treatmentType, i) => (
              <Checkbox.Root
                key={i}
                colorPalette="primary"
                name={treatmentType}
                checked={selectedItems.has(treatmentType)}
                onCheckedChange={({ checked }) => {
                  setSelectedItems((prev) => {
                    if (checked) {
                      prev.add(treatmentType);
                    } else {
                      prev.delete(treatmentType);
                    }
                    return new Set(prev);
                  });
                }}
              >
                <Checkbox.HiddenInput />
                <Checkbox.Control />
                <Checkbox.Label>
                  <Text fontSize="sm">
                    {t(`sdk:treatments.${treatmentType}`)}
                  </Text>
                </Checkbox.Label>
              </Checkbox.Root>
            ))}
          </VStack>
        </Popover.Body>
        <Popover.Footer display="flex" justifyContent="flex-end">
          <ButtonGroup colorPalette="primary" size="sm">
            <Button onClick={handleClear} disabled={!selectedItems.size}>
              {t("map:filters.clearButton")}
            </Button>
            <Button onClick={handleSubmit}>
              {t("map:filters.submitButton")}
            </Button>
          </ButtonGroup>
        </Popover.Footer>
      </CustomPopover>

      {!!filterStore.treatmentFilters.length && (
        <Box m={-1}>
          {filterStore.treatmentFilters.map((treatmentType, i) => (
            <FilterTag
              key={i}
              onClose={() => removeActiveFilter(treatmentType)}
            >
              {t(`sdk:treatments.${treatmentType}`)}
            </FilterTag>
          ))}
        </Box>
      )}
    </Stack>
  );
};

export const TreatmentFilters = observer(TreatmentFiltersBase);
